import React, { FC, useEffect, useState } from 'react';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import {
  LocalizationProvider,
  StaticDatePicker,
  PickersDay,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { formatFilmDayList } from '../../utils/filmWindow';
import { APP_TOOLTIP_PROPS, TOOLTIP_EYEBROW_SX } from '../../utils';

interface Props {
  disabled?: boolean;
  onSelect?: (date: Dayjs) => void;
  initialDate?: string | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  shouldDisableDate?: (date: Dayjs) => boolean;
  caption?: string;
  label?: string;
  value?: Dayjs | null;
  bare?: boolean;
  placeholder?: string;
  starredDays?: Record<string, string[]>;
  legend?: string;
  selectedDays?: string[];
  maxDays?: number;
  onToggleDay?: (dayKey: string) => void;
}

// `picked` (multi-day mode) is applied here, not as `selected` in slotProps:
// the calendar sets selected/aria-selected from its single-date value AFTER
// slotProps, so a picked day read as unselected to screen readers.
const DayWithPreference = ({ preferredBy, picked, ...dayProps }: any) => {
  const day = (
    <PickersDay {...dayProps} {...(picked ? { selected: true, 'aria-selected': true } : {})} />
  );
  if (!preferredBy || preferredBy.length === 0) return day;
  return (
    <Tooltip
      arrow
      placement="top"
      componentsProps={APP_TOOLTIP_PROPS}
      title={
        <Box>
          <Box component="span" sx={TOOLTIP_EYEBROW_SX}>
            Preferred by
          </Box>
          {preferredBy.join(', ')}
        </Box>
      }
    >
      {day}
    </Tooltip>
  );
};

const StyledDatePicker = styled(StaticDatePicker)({
  border: '1px solid var(--border)',
  borderRadius: '16px 16px 0px 0px',
  width: '100%',
  minWidth: 280,
  '& .MuiPickersToolbar-root': {
    display: 'none',
  },
  '& .MuiDateCalendar-root': {
    width: 310,
  },
});

export const ExpertCalendar: FC<Props> = ({
  disabled,
  onSelect,
  initialDate,
  minDate,
  maxDate,
  shouldDisableDate,
  caption,
  label = 'Select Filming Date',
  value,
  bare = false,
  placeholder = 'Select a date',
  starredDays,
  legend,
  selectedDays,
  maxDays,
  onToggleDay,
}) => {
  const multi = Array.isArray(selectedDays);
  const controlled = value !== undefined;
  const [internalDate, setInternalDate] = useState<Dayjs | undefined>(
    initialDate ? dayjs(initialDate) : undefined
  );
  const date = controlled ? value || undefined : internalDate;

  useEffect(() => {
    if (multi || controlled) return;
    if (initialDate && !internalDate) {
      const d = dayjs(initialDate);
      setInternalDate(d);
      onSelect?.(d);
    }
  }, [initialDate]);

  const handleChange = (next: Dayjs) => {
    if (multi) return;
    if (!controlled) setInternalDate(next);
    onSelect?.(next);
  };

  // maxDays > 1: the at-cap freeze only makes sense in multi-day mode, where
  // the deselect chips provide removal. With a single film day it froze the
  // whole calendar the moment the first day was picked — the wrong day could
  // never be corrected. Single-day replacement is handled by toggleDay.
  const capReached =
    multi &&
    maxDays != null &&
    maxDays > 1 &&
    (selectedDays as string[]).length >= maxDays;
  const isDayDisabled = (day: Dayjs) => {
    if (shouldDisableDate?.(day)) return true;
    if (!capReached) return false;
    return !(selectedDays as string[]).includes(day.format('YYYY-MM-DD'));
  };

  return (
    <Box
      className={
        bare
          ? 'flex flex-col gap-3 w-full items-center'
          : 'flex flex-col gap-4 w-full p-8 border-r border-border-token items-center'
      }
    >
      {label && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'var(--text-2)', textTransform: 'uppercase', alignSelf: 'flex-start' }}>
          {label}
        </Typography>
      )}
      <Box className="flex flex-col w-full">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
          <StyledDatePicker
            disabled={disabled}
            value={multi ? null : date || null}
            minDate={minDate || dayjs()}
            maxDate={maxDate}
            shouldDisableDate={
              shouldDisableDate || capReached
                ? (day: unknown) => isDayDisabled(day as Dayjs)
                : undefined
            }
            onChange={handleChange as (value: unknown) => void}
            slots={{ day: DayWithPreference }}
            slotProps={{
              actionBar: { actions: [] },
              day: (ownerState: any) => {
                const day: Dayjs | undefined = ownerState?.day;
                const key = day ? day.format('YYYY-MM-DD') : '';
                const names = starredDays ? starredDays[key] : undefined;
                const starred = !!names && names.length > 0;
                const picked = multi && (selectedDays as string[]).includes(key);
                const sx: Record<string, unknown> = {};
                if (picked) {
                  Object.assign(sx, {
                    border: '1.5px solid #A58450',
                    fontWeight: 700,
                    color: '#7A5C24',
                    backgroundColor: 'transparent',
                    '&.MuiPickersDay-today': {
                      border: '1.5px solid #A58450',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      color: '#7A5C24',
                    },
                    '&:hover, &.Mui-selected:hover, &.Mui-selected:focus': {
                      backgroundColor: '#F5EADA',
                      color: '#7A5C24',
                    },
                  });
                }
                if (starred) {
                  Object.assign(sx, {
                    position: 'relative',
                    overflow: 'visible',
                    fontWeight: 700,
                    '&:not(.Mui-selected)': {
                      backgroundColor: '#F5EADA',
                      color: '#7A5C24',
                      '&:hover': { backgroundColor: '#EDDCC4' },
                    },
                    '&::after': {
                      content: '"★"',
                      position: 'absolute',
                      top: 1,
                      right: 2,
                      fontSize: 9,
                      lineHeight: 1,
                      color: '#A58450',
                      textShadow: '0 0 2px #fff, 0 0 2px #fff',
                      pointerEvents: 'none',
                    },
                  });
                }
                const dayProps: Record<string, unknown> = {};
                if (multi) {
                  dayProps.onDaySelect = (clicked: Dayjs) =>
                    onToggleDay?.(clicked.format('YYYY-MM-DD'));
                }
                if (picked) dayProps.picked = true;
                if (starred) dayProps.preferredBy = names;
                if (Object.keys(sx).length > 0) dayProps.sx = sx;
                return dayProps;
              },
            }}
          />
        </LocalizationProvider>
        <Box className="flex flex-col items-center justify-center bg-surface-2 rounded-lg rounded-tr-none rounded-tl-none p-4 border border-t-0 border-border-token">
          <Typography variant="body1">
            {multi
              ? formatFilmDayList(selectedDays as string[]) || placeholder
              : date?.format('MMM DD, YYYY') || placeholder}
          </Typography>
          {caption && (
            <Typography sx={{ fontSize: 11, color: '#8A8A8A', mt: 0.5, textAlign: 'center' }}>
              {caption}
            </Typography>
          )}
          {legend && (
            <Typography
              sx={{
                fontSize: 10.5,
                color: '#A58450',
                mt: 0.75,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box component="span" sx={{ fontSize: 11, lineHeight: 1 }}>
                ★
              </Box>
              {legend}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
